import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthProvider';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { CircularProgress } from '@mui/material';

const ImpersonationBanner = () => {
  const { auth, setAuth } = useContext(AuthContext);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [remainingTime, setRemainingTime] = useState(null);
  const [isEnding, setIsEnding] = useState(false);

  const isImpersonating = auth?.isImpersonating || localStorage.getItem('isImpersonating') === 'true';

  useEffect(() => {
    if (!isImpersonating) return;

    const expiresAt = auth?.impersonationExpiresAt || localStorage.getItem('impersonationExpiresAt');
    if (!expiresAt) return;

    const updateRemainingTime = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diffMs = expiry - now;

      if (diffMs <= 0) {
        handleSessionExpired();
        return;
      }

      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setRemainingTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [isImpersonating, auth?.impersonationExpiresAt]);

  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem('auth');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('impersonationSessionId');
    localStorage.removeItem('impersonationExpiresAt');
    setAuth({});
    navigate('/impersonation-ended', { replace: true });
  }, [setAuth, navigate]);

  const handleEndSession = async () => {
    setIsEnding(true);
    try {
      const sessionId = auth?.impersonationSessionId || localStorage.getItem('impersonationSessionId');
      if (sessionId) {
        await axiosPrivate.post('/Impersonation/EndSession', { sessionId });
      }
    } catch (error) {
      console.error('Error ending impersonation session:', error);
    } finally {
      localStorage.removeItem('auth');
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonationSessionId');
      localStorage.removeItem('impersonationExpiresAt');
      setAuth({});
      navigate('/impersonation-ended', { replace: true });
    }
  };

  if (!isImpersonating) {
    return null;
  }

  const impersonatorName = auth?.impersonatorName || 'Admin';

  return (
    <div
      className="impersonation-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'linear-gradient(90deg, #6b8cff 0%, #5a7dee 100%)',
        color: 'white',
        padding: '8px 16px',
        fontSize: '14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <i className="bi bi-eye-fill me-2"></i>
          <span>
            <strong>View-Only Mode</strong> - You are viewing as {auth?.fullName || 'Staff'}.
            Viewing by: {impersonatorName}
          </span>
        </div>
        <div className="d-flex align-items-center">
          {remainingTime && (
            <span className="me-3" style={{ opacity: 0.9 }}>
              <i className="bi bi-clock me-1"></i>
              {remainingTime} remaining
            </span>
          )}
          <button
            className="btn btn-sm btn-light"
            onClick={handleEndSession}
            disabled={isEnding}
            style={{ fontWeight: 500 }}
          >
            {isEnding ? (
              <>
                <CircularProgress size={16} sx={{ color: 'inherit' }} className="me-1" />
                Ending...
              </>
            ) : (
              <>
                <i className="bi bi-x-lg me-1"></i>
                End Session
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
